import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import MarkdownView from "@/components/MarkdownView";
import { ArrowRight, ChevronLeft, Lock, ExternalLink, Video, Headphones, Github } from "lucide-react";

function VideoEmbed({ url }) {
  if (!url) return null;
  // crude YouTube/Vimeo detector — fallback to native video
  if (url.includes("youtu")) {
    const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([\w-]{11})/);
    if (m) {
      return (
        <div className="aspect-video bg-black rounded-sm overflow-hidden">
          <iframe src={`https://www.youtube.com/embed/${m[1]}`} className="w-full h-full" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope" allowFullScreen title="Explainer video" />
        </div>
      );
    }
  }
  return (
    <video controls className="w-full rounded-sm" data-testid="explainer-video">
      <source src={url} />
    </video>
  );
}

export default function ExplainerDetail() {
  const { id } = useParams();
  const [ex, setEx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setErr(null);
    api.get(`/paper-explainers/${id}`)
      .then((r) => { if (!cancelled) setEx(r.data); })
      .catch((e) => { if (!cancelled) setErr(e?.response?.data?.detail || "Not found"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id]);

  if (loading) return <div className="min-h-screen flex items-center justify-center kb-mono text-xs text-kb-ink-muted">Loading…</div>;
  if (err || !ex) {
    return (
      <div className="min-h-screen bg-kb-bg">
        <Navbar />
        <main className="max-w-3xl mx-auto px-6 pt-20 pb-24 text-center">
          <div className="kb-mono text-xs text-kb-ink-muted">{err || "Explainer not found"}</div>
          <Link to="/explainers" className="kb-btn-outline text-sm mt-4 inline-flex"><ChevronLeft className="w-3.5 h-3.5" /> Back to library</Link>
        </main>
        <Footer />
      </div>
    );
  }

  const Icon = ex.format === "audio" ? Headphones : Video;
  return (
    <div className="min-h-screen bg-kb-bg" data-testid="explainer-detail-page">
      <Navbar />
      <main className="max-w-4xl mx-auto px-6 pt-10 pb-24">
        <Link to="/explainers" className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted inline-flex items-center gap-1 hover:text-kb-primary" data-testid="explainer-back-link">
          <ChevronLeft className="w-3.5 h-3.5" /> Paper Explainers
        </Link>

        <div className="mt-4 flex items-center gap-2 flex-wrap">
          <Icon className="w-4 h-4 text-kb-primary" />
          <span className="kb-mono text-[10px] uppercase tracking-wider text-kb-primary">{ex.format}</span>
          <span className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">{ex.duration_min} MIN</span>
          {ex.is_premium && (
            <span className="kb-mono text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm bg-kb-primary/10 text-kb-primary inline-flex items-center gap-1">
              <Lock className="w-3 h-3" /> PREMIUM
            </span>
          )}
        </div>

        <h1 className="font-serif text-3xl lg:text-4xl text-kb-ink mt-3 leading-tight">{ex.paper_title}</h1>
        <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted mt-2">
          {(ex.paper_authors || []).join(", ")}{ex.paper_year ? ` · ${ex.paper_year}` : ""}
          {ex.paper_doi ? ` · DOI ${ex.paper_doi}` : ""}
        </div>
        {ex.paper_url && (
          <a href={ex.paper_url} target="_blank" rel="noreferrer" className="kb-mono text-[10px] uppercase tracking-wider text-kb-primary inline-flex items-center gap-1 mt-2">
            Read original paper <ExternalLink className="w-3 h-3" />
          </a>
        )}

        {ex.summary && (
          <section className="mt-6 kb-card p-6">
            <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">SUMMARY</div>
            <p className="mt-2 text-base text-kb-ink leading-relaxed">{ex.summary}</p>
          </section>
        )}

        {ex.locked ? (
          <section className="mt-6 kb-card p-8 text-center border-2 border-dashed border-kb-primary/40 bg-kb-primary/5" data-testid="explainer-paywall">
            <Lock className="w-8 h-8 text-kb-primary mx-auto" strokeWidth={1.4} />
            <h3 className="font-serif text-2xl text-kb-ink mt-3">Unlock the full explainer</h3>
            <p className="text-sm text-kb-ink-muted mt-2 max-w-md mx-auto">
              Premium subscribers get the full {ex.format === "audio" ? "audiobook" : "video"}, transcript, and code repository.
            </p>
            <Link to="/pricing" className="kb-btn-primary mt-5 inline-flex" data-testid="explainer-pricing-cta">
              View pricing plans <ArrowRight className="w-4 h-4" />
            </Link>
          </section>
        ) : (
          <>
            {ex.video_url && ex.format === "video" && (
              <section className="mt-6">
                <VideoEmbed url={ex.video_url} />
              </section>
            )}
            {ex.audio_url && (
              <section className="mt-6 kb-card p-6">
                <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted mb-3">AUDIOBOOK</div>
                <audio controls className="w-full" data-testid="explainer-audio">
                  <source src={ex.audio_url} />
                </audio>
              </section>
            )}
            {ex.transcript_md && (
              <section className="mt-6 kb-card p-6">
                <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">TRANSCRIPT & NOTES</div>
                <div className="mt-3"><MarkdownView source={ex.transcript_md} /></div>
              </section>
            )}
            {ex.code_repo_url && (
              <section className="mt-6 kb-card p-6 flex items-center justify-between">
                <div>
                  <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">CODE REPOSITORY</div>
                  <div className="text-sm text-kb-ink mt-1 break-all">{ex.code_repo_url}</div>
                </div>
                <a href={ex.code_repo_url} target="_blank" rel="noreferrer" className="kb-btn-outline text-sm" data-testid="explainer-code-link">
                  <Github className="w-3.5 h-3.5" /> Open repo
                </a>
              </section>
            )}
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
